import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLive } from "@/lib/useLive";
import { TicketSlide, fDate, QrZoomDialog } from "@/components/TicketSlide";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { SlideCarousel } from "@/components/SlideCarousel";
import { isUrgentTicket } from "@/lib/urgentTickets";
import { saveOfflineTickets, getOfflineTickets, prefetchSessionQrs, isNetworkError } from "@/lib/offline";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Star, Ticket as TicketIcon, AlertTriangle, WifiOff } from "lucide-react";
import { RateMaestroDialog } from "@/components/MaestroRating";
import { GiftsDelivered } from "@/components/GiftsDelivered";
import { PushToggle } from "@/components/PushToggle";
import { useSearchParams } from "react-router-dom";

function QrFromLink({ active }) {
  const [params, setParams] = useSearchParams();
  const [rid, sid] = (params.get("qr") || "").split(".");
  const r = active?.find((x) => x.id === rid);
  const i = r?.sessions?.findIndex((s) => s.id === sid) ?? -1;
  if (!r || i < 0 || !r.sessions[i].qr_url) return null;
  const close = () => { params.delete("qr"); setParams(params, { replace: true }); };
  return <QrZoomDialog r={r} s={r.sessions[i]} n={i + 1} onClose={close} />;
}

const STATUS_LABEL = {
  confirmed: { t: "Finalizado", c: "bg-muted text-foreground" },
  refunded: { t: "Cancelada", c: "bg-muted text-muted-foreground" },
  business_cancelled: { t: "Cancelada por el taller", c: "bg-destructive text-destructive-foreground" },
};

function TicketsCarousel({ items, onCancel, onChanged }) {
  return <SlideCarousel items={items} prefix="tickets" label="Ticket"
    render={(r) => <TicketSlide r={r} onCancel={onCancel} onChanged={onChanged} />} />;
}

export default function MyTickets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user?.role === "business" || user?.role === "super_admin") navigate(user.role === "business" ? "/maestro" : "/admin", { replace: true }); }, [user, navigate]);
  const [data, setData] = useState(null);
  const [offlineAt, setOfflineAt] = useState(null);

  const show = (d) => {
    const urgent = (d.active || []).filter(isUrgentTicket);
    setData({ ...d, active: [...urgent, ...(d.active || []).filter((t) => !isUrgentTicket(t))], urgent: urgent.length });
  };

  const load = () => {
    return api.get("/reservations/mine", { offlineFallback: true }).then((r) => {
      show(r.data);
      setOfflineAt(null);
      saveOfflineTickets(user.id, r.data);
      prefetchSessionQrs(r.data.active);
      window.dispatchEvent(new Event("tickets-changed"));
    })
      .catch((e) => {
        const cached = isNetworkError(e) && getOfflineTickets(user.id);
        if (cached) { show(cached); setOfflineAt(cached.saved_at); return; }
        toast.error(formatApiErrorDetail(e.response?.data?.detail)); setData({ active: [], history: [] });
      });
  };
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line
  useLive(() => api.get("/reservations/mine").then((r) => { show(r.data); saveOfflineTickets(user.id, r.data); }), 10000, !!user);

  const cancel = async (r) => {
    try {
      await api.post(`/reservations/${r.id}/cancel`);
      toast.success("Reserva cancelada. Tu cupo quedó liberado.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="tickets-login-required">
        <TicketIcon className="w-10 h-10 mx-auto mb-3 text-primary" />
        <h1 className="text-2xl font-extrabold">Mis Tickets</h1>
        <p className="text-muted-foreground mt-2">Inicia sesión para ver tus tickets. Si reservaste como invitado, tu ticket está en el correo de confirmación.</p>
        <Button asChild className="mt-6"><Link to="/login?next=/mis-tickets" data-testid="tickets-login-btn">Iniciar sesión</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-extrabold tracking-tight">Mis Tickets</h1>
      <p className="text-muted-foreground mt-1">Tus talleres próximos y en curso. Desliza para ver cada ticket.</p>
      <PendingMaestroReviews />
      <PushToggle />
      {data && <QrFromLink active={data.active} />}
      {offlineAt && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm dark:bg-amber-950/40 dark:text-amber-200" data-testid="tickets-offline-banner">
          <WifiOff className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Sin conexión. Mostramos tus tickets guardados el {fDate(offlineAt)} a las {new Date(offlineAt).toLocaleTimeString("es-CL", { timeZone: "America/Santiago", hour: "2-digit", minute: "2-digit", hour12: false })} hrs. Tus QR siguen siendo válidos.</span>
        </div>
      )}

      {!data ? <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mt-16" /> : (
        <>
          <div className="mt-6"><GiftsDelivered /></div>
          {data.urgent > 0 && (
            <div className="mt-6 flex items-start gap-2.5 rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200" data-testid="tickets-urgent-banner">
              <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
              <span>Tienes {data.urgent} {data.urgent === 1 ? "taller" : "talleres"} con la próxima sesión en menos de 24 horas sin confirmar. Confírmala en el ticket para asegurar tu participación.</span>
            </div>
          )}
          <div className="mt-8">
            {data.active.length ? <TicketsCarousel items={data.active} onCancel={cancel} onChanged={load} /> : (
              <div className="text-center py-16 text-muted-foreground" data-testid="tickets-empty">
                <TicketIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No tienes talleres próximos.
                <div><Button asChild variant="link"><Link to="/explorar">Explorar talleres</Link></Button></div>
              </div>
            )}
          </div>
          {data.history.length > 0 && (
            <section className="mt-12" data-testid="tickets-history">
              <h2 className="text-base md:text-lg font-bold mb-3">Historial</h2>
              <div className="divide-y rounded-2xl border bg-card">
                {data.history.map((r) => {
                  const st = STATUS_LABEL[r.status] || { t: r.status, c: "bg-muted" };
                  return (
                    <div key={r.id} className="flex items-center gap-3 p-3.5" data-testid={`history-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{r.workshop_name}</p>
                        <p className="text-xs text-muted-foreground">{r.business?.name} · {fDate(r.first_session_at)} al {fDate(r.last_session_at)}</p>
                      </div>
                      <Badge className={`${st.c} shrink-0`}>{st.t}</Badge>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

    </div>
  );
}

function PendingMaestroReviews() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/me/pending-maestro-reviews").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items.length) return null;
  return (
    <div className="mt-4 rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-4" data-testid="pending-maestro-reviews">
      <p className="font-bold text-sm flex items-center gap-1.5"><Star className="w-4 h-4 fill-amber-400 text-amber-400" />¡Evalúa a tus Maestros!</p>
      <p className="text-xs text-muted-foreground mb-2">Cumpliste el 75% de asistencia. Tu evaluación ayuda a otros alumnos.</p>
      <div className="space-y-2">
        {items.map((x) => (
          <div key={x.reservation_id} className="flex items-center justify-between gap-2">
            <span className="text-sm truncate">{x.business_name} · {x.workshop_name}</span>
            <Button size="sm" onClick={() => setOpen(x)} data-testid={`pending-rate-${x.reservation_id}`}>Evaluar</Button>
          </div>
        ))}
      </div>
      <RateMaestroDialog item={open} onClose={() => setOpen(null)} onDone={load} />
    </div>
  );
}
